/**
 * 500 Internal Server Error Page
 * Displayed when a server error occurs
 */
import React from 'react';
import { Link, useRouteError, isRouteErrorResponse } from 'react-router';
import { ServerCrash, Home, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { logger } from '../../lib/logger';

export function ServerError() {
  const error = useRouteError();

  const handleRefresh = () => {
    window.location.reload();
  };

  // Extract error message safely
  let errorMessage = 'An unexpected error occurred';
  let errorStack: string | undefined;

  if (isRouteErrorResponse(error)) {
    errorMessage = error.statusText || `Error ${error.status}`;
  } else if (error instanceof Error) {
    errorMessage = error.message;
    errorStack = error.stack;
  } else if (typeof error === 'string') {
    errorMessage = error;
  }

  // Log error for debugging
  logger.error('Route Error:', error);
  logger.error('Error Message:', errorMessage);

  // Check if this is specifically a chunk loading error (happens after deployments)
  // Only these specific errors should trigger auto-refresh
  const isChunkLoadError =
    errorMessage.includes('Failed to fetch dynamically imported module') ||
    errorMessage.includes('Loading chunk') ||
    errorMessage.includes('Loading CSS chunk') ||
    errorMessage.includes('error loading dynamically imported module');

  // Auto-refresh ONLY for actual chunk loading failures
  React.useEffect(() => {
    if (isChunkLoadError) {
      const timer = setTimeout(() => {
        window.location.reload();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isChunkLoadError]);

  // Show updating message only for genuine chunk errors
  if (isChunkLoadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#050505] p-6">
        <div className="text-center max-w-lg">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-blue-100 dark:bg-blue-500/10 mb-8">
            <RefreshCw className="w-12 h-12 text-blue-500 dark:text-blue-400 animate-spin" />
          </div>

          <h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-300 mb-6">
            Updating Application...
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
            A new version is available. The page will refresh automatically.
          </p>
        </div>
      </div>
    );
  }

  // Standard error page for all other errors
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#050505] p-6">
      <div className="text-center max-w-lg">
        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-red-100 dark:bg-red-500/10 mb-8">
          <ServerCrash className="w-12 h-12 text-red-500 dark:text-red-400" />
        </div>

        <h1 className="text-7xl font-bold text-gray-900 dark:text-white mb-4">500</h1>
        <h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-300 mb-6">
          Server Error
        </h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
          Something went wrong on our end. Please try again later or contact support if the problem persists.
        </p>

        {/* Show error details for debugging */}
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-left overflow-auto max-h-48">
          <p className="text-sm font-mono text-red-600 dark:text-red-400">
            {errorMessage}
          </p>
          {errorStack && (
            <pre className="text-xs mt-2 text-red-500 dark:text-red-300 whitespace-pre-wrap">
              {errorStack}
            </pre>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            variant="outline"
            className="border-gray-200 dark:border-white/10"
            onClick={handleRefresh}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
          <Button asChild className="bg-fuchsia-600 hover:bg-fuchsia-700">
            <Link to="/" className="flex items-center gap-2">
              <Home className="w-4 h-4" />
              Dashboard
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ServerError;
